import { useRef, useState } from "react";
import { FileVideo, Upload, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import { parseSubtitles } from "@/lib/subtitles";

const EXT = /\.(mp4|webm|mov|m4v|mkv|ogv|3gp)$/i;
const MAX_MB = 500;
const MAX_SEC = 600;

function probe(url) {
  return new Promise((res) => {
    const v = document.createElement("video");
    const to = setTimeout(() => res(null), 15000);
    v.preload = "metadata";
    v.muted = true;
    v.onloadedmetadata = () => {
      const done = () => { clearTimeout(to); res({ duration: v.duration, w: v.videoWidth || 16, h: v.videoHeight || 9 }); };
      if (isFinite(v.duration)) return done();
      v.ondurationchange = () => isFinite(v.duration) && done();
      v.currentTime = 1e9;
    };
    v.onerror = () => { clearTimeout(to); res(null); };
    v.src = url;
  });
}

export async function validateVideo(f) {
  if (!(f.type.startsWith("video/") || EXT.test(f.name))) return { error: `"${f.name}" is not a supported video. Please upload MP4, WebM or MOV.` };
  if (f.size === 0) return { error: "This file is empty." };
  if (f.size > MAX_MB * 1048576) return { error: `File is ${(f.size / 1048576).toFixed(0)} MB — the limit is ${MAX_MB} MB.` };
  const url = URL.createObjectURL(f);
  const meta = await probe(url);
  if (!meta || !meta.w) {
    URL.revokeObjectURL(url);
    return { error: "Your browser cannot play this video format/codec. Try converting it to MP4 (H.264 + AAC)." };
  }
  if (meta.duration > MAX_SEC) {
    URL.revokeObjectURL(url);
    return { error: `Video is ${(meta.duration / 60).toFixed(1)} min long — the limit is 10 minutes.` };
  }
  return { url, meta };
}

export const UploadPanel = ({ file, meta, onVideo, disabled, onImport }) => {
  const inputRef = useRef(null);
  const subRef = useRef(null);
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);

  const pick = async (f) => {
    if (!f) return;
    setError(null);
    setChecking(true);
    const r = await validateVideo(f);
    setChecking(false);
    if (r.error) return setError(r.error);
    onVideo({ file: f, url: r.url, meta: r.meta });
    toast.success("Video loaded locally — nothing was uploaded.");
  };

  const importSubs = async (f) => {
    if (!f) return;
    const caps = parseSubtitles(await f.text());
    if (!caps.length) return toast.error("No valid captions found in that file.");
    onImport(caps);
    toast.success(`Imported ${caps.length} captions from ${f.name}`);
  };

  return (
    <div className="space-y-3">
      <h2 className="text-xs font-semibold tracking-wider uppercase text-[var(--cc-text-2)]">1 · Upload video</h2>
      <input ref={inputRef} type="file" accept="video/*,.mp4,.webm,.mov,.m4v,.mkv" className="hidden" data-testid="video-file-input"
        onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
      <button onClick={() => inputRef.current.click()} disabled={disabled || checking} data-testid="upload-video-btn"
        className="w-full min-h-[96px] rounded-lg border-2 border-dashed border-[var(--cc-border-strong)] hover:border-[var(--cc-accent)] bg-[var(--cc-panel)] flex flex-col items-center justify-center gap-1.5 px-4 py-4 disabled:opacity-50 transition-colors">
        <Upload className="h-6 w-6 text-[var(--cc-accent)]" />
        <span className="font-bold">{checking ? "Checking video…" : file ? "Upload a different video" : "Upload Video"}</span>
        <span className="text-xs text-[var(--cc-text-2)]">MP4, WebM, MOV · max 500 MB · max 10 min · stays on your device</span>
      </button>
      {error && (
        <div role="alert" data-testid="upload-error" className="flex gap-2 p-3 rounded-md bg-rose-500/10 border border-rose-500/40 text-sm text-rose-200">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> {error}
        </div>
      )}
      {file && (
        <div className="flex items-center gap-3 p-3 rounded-md bg-[var(--cc-elev)] text-sm" data-testid="file-info">
          <FileVideo className="h-5 w-5 text-[var(--cc-accent)] shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold truncate">{file.name}</p>
            <p className="text-xs text-[var(--cc-text-2)] font-mono-ts">{(file.size / 1048576).toFixed(1)} MB · {meta.duration.toFixed(1)}s · {meta.w}×{meta.h}</p>
          </div>
        </div>
      )}
      <input ref={subRef} type="file" accept=".srt,.vtt,text/plain" className="hidden" data-testid="import-srt-input"
        onChange={(e) => { importSubs(e.target.files[0]); e.target.value = ""; }} />
      <button onClick={() => subRef.current.click()} data-testid="import-srt-btn"
        className="text-xs font-semibold text-[var(--cc-text-2)] hover:text-white inline-flex items-center gap-1.5 h-9">
        <FileText className="h-4 w-4" /> Already have subtitles? Import SRT / VTT
      </button>
    </div>
  );
};
